import React from 'react';
import { Box, Typography, Grid, Container } from '@mui/material';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

const flowSteps = [
  { icon: FingerprintIcon, iconColor: '#059669', bg: '#f0fdf4', title: 'Employee Punches', desc: 'Fingerprint / face scan at any branch or remote location.', label: 'Biometric Device', num: '01' },
  { icon: CloudSyncIcon, iconColor: '#0284c7', bg: '#eff6ff', title: 'Data Syncs Instantly', desc: 'Punch data flows to the cloud server in real-time, no manual import needed.', label: 'Cloud Sync', num: '02' },
  { icon: AccountBalanceWalletIcon, iconColor: '#7c3aed', bg: '#f5f3ff', title: 'Payroll Auto-Calculated', desc: 'Salary, OT, deductions, and allowances computed based on attendance data.', label: 'Payroll Engine', num: '03' },
  { icon: ReceiptLongIcon, iconColor: '#ea580c', bg: '#fff7ed', title: 'Payslip Generated & Sent', desc: 'PDF payslips emailed to all employees with one click on pay day.', label: 'Payslip Delivery', num: '04' },
];

const features = [
  { title: 'Any Brand Device Support', desc: 'Works with ZKTeco, Mantra, eSSL and all major biometric brands out of the box.', color: '#10b981' },
  { title: 'Multi-Branch Management', desc: 'Manage attendance from all your offices under a single unified dashboard.', color: '#0ea5e9' },
  { title: 'Shift Management', desc: 'Configure day/night/rotational shifts and overtime rules per department.', color: '#8b5cf6' },
  { title: 'Leave Management', desc: 'Full leave application, approval, balance, and carry-forward management.', color: '#f59e0b' },
  { title: 'PF, ESI & PT Compliance', desc: 'Statutory reports auto-generated every month for PF, ESI, and Professional Tax.', color: '#ef4444' },
  { title: 'Bank Transfer File Export', desc: 'Export salary register in bank-ready formats like XLS, CSV, or NEFT file.', color: '#059669' },
];

const HowWorks = () => (
  <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: '#f8fafc' }}>
    <Container maxWidth="lg">
      {/* Section header */}
      <Box sx={{ textAlign: 'center', mb: 8 }}>
        <Typography sx={{ color: '#059669', fontWeight: 700, fontSize: 13, letterSpacing: 2, mb: 2, fontFamily: "'Montserrat', sans-serif", textTransform: 'uppercase' }}>How It Works</Typography>
        <Typography variant="h2" sx={{ fontWeight: 900, color: '#0f172a', fontFamily: "'Montserrat', sans-serif", fontSize: { xs: '2rem', md: '2.8rem' } }}>
          From Device to Payslip — Fully Automated
        </Typography>
      </Box>

      {/* Flow steps */}
      <Grid container spacing={3} sx={{ mb: 10 }}>
        {flowSteps.map((s, i) => {
          const IconComponent = s.icon;
          return (
            <Grid item xs={12} sm={6} md={3} key={i} sx={{ display: 'flex' }}>
              <Box sx={{
                flex: 1, textAlign: 'center', bgcolor: '#ffffff',
                borderRadius: '24px', p: 4,
                border: '1px solid #e2e8f0',
                boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
                borderTop: `4px solid ${s.iconColor}`,
                transition: 'all 0.3s',
                '&:hover': { transform: 'translateY(-6px)', boxShadow: '0 20px 40px rgba(0,0,0,0.07)' },
                display: 'flex', flexDirection: 'column', alignItems: 'center',
              }}>
                {/* Step number */}
                <Box sx={{ width: 32, height: 32, borderRadius: '10px', bgcolor: s.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2, border: `1px solid ${s.iconColor}30` }}>
                  <Typography sx={{ color: s.iconColor, fontWeight: 900, fontSize: 13, fontFamily: "'Montserrat', sans-serif" }}>{s.num}</Typography>
                </Box>
                {/* Icon */}
                <Box sx={{ width: 80, height: 80, borderRadius: '20px', bgcolor: s.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
                  <IconComponent sx={{ fontSize: 38, color: s.iconColor }} />
                </Box>
                <Typography sx={{ color: s.iconColor, fontSize: 11, fontWeight: 800, letterSpacing: 1.2, fontFamily: "'Montserrat', sans-serif", mb: 1.5, textTransform: 'uppercase' }}>{s.label}</Typography>
                <Typography sx={{ color: '#0f172a', fontWeight: 800, fontFamily: "'Montserrat', sans-serif", fontSize: '1rem', mb: 1.5 }}>{s.title}</Typography>
                <Typography sx={{ color: '#64748b', fontSize: 14, fontFamily: "'Montserrat', sans-serif", lineHeight: 1.7 }}>{s.desc}</Typography>
                {i < flowSteps.length - 1 && <ArrowDownwardIcon sx={{ color: '#cbd5e1', mt: 3, display: { md: 'none' } }} />}
              </Box>
            </Grid>
          );
        })}
      </Grid>

      {/* All Features Included */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography sx={{ color: '#059669', fontWeight: 700, fontSize: 13, letterSpacing: 2, mb: 1.5, fontFamily: "'Montserrat', sans-serif", textTransform: 'uppercase' }}>What's Included</Typography>
        <Typography variant="h3" sx={{ fontWeight: 900, color: '#0f172a', fontFamily: "'Montserrat', sans-serif", fontSize: { xs: '1.8rem', md: '2.4rem' } }}>All Features Included</Typography>
      </Box>

      <Grid container spacing={3}>
        {features.map((f, i) => (
          <Grid item xs={12} sm={6} md={4} key={i} sx={{ display: 'flex' }}>
            <Box sx={{
              flex: 1, bgcolor: '#ffffff',
              borderTop: `4px solid ${f.color}`,
              borderRadius: '16px', p: 3.5,
              border: '1px solid #f1f5f9',
              boxShadow: '0 4px 15px rgba(0,0,0,0.02)',
              transition: 'all 0.3s',
              '&:hover': { transform: 'translateY(-5px)', boxShadow: '0 15px 35px rgba(0,0,0,0.05)' },
              display: 'flex', flexDirection: 'column',
            }}>
              {/* Color dot accent */}
              <Box sx={{ width: 32, height: 32, borderRadius: '10px', bgcolor: `${f.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: f.color }} />
              </Box>
              <Typography sx={{ fontWeight: 800, color: '#0f172a', fontSize: '1.05rem', mb: 1.5, fontFamily: "'Montserrat', sans-serif" }}>{f.title}</Typography>
              <Typography sx={{ color: '#64748b', fontSize: 14.5, fontFamily: "'Montserrat', sans-serif", lineHeight: 1.7 }}>{f.desc}</Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Container>
  </Box>
);

export default HowWorks;
